"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"

import { useFetchCurrentClientIntegrations, useGetCurrentClientAuthUri } from "@/hooks/api/current-client-integrations"
import { useFetchDialpadIntegrations, useGetDialpadAuthUri } from "@/hooks/api/dialpad-integrations"
import { useGetAuthUriMutation as useGetEmailAuthUriMutation } from "@/hooks/api/email_integrations"
import { useEmailIntegrations } from "@/hooks/api/email_integrations/queries"
import { useFetchGoToIntegrations, useGetGoToAuthUri } from "@/hooks/api/go-to-integrations"
import { useFetchIntulseIntegrations, useGetIntulseAuthUri } from "@/hooks/api/intulse-integrations"
import { useFetchMyRepChatIntegrations } from "@/hooks/api/my-rep-chat-integrations"
import { useFetchRingCentralIntegrations, useGetRingCentralAuthUri } from "@/hooks/api/ring-central-integrations"
import { useFetchZoomPhoneIntegrations, useGetZoomPhoneAuthUri } from "@/hooks/api/zoom-phone-integrations"
import { IntegrationBanner } from "@/components/layout/integration-banner"

const DISMISSED_KEY_PREFIX = "integration-banner-dismissed"

function dismissedKey(integration: string) {
  return `${DISMISSED_KEY_PREFIX}:${integration}`
}

function isDismissedInSession(key: string) {
  if (typeof window === "undefined") return false
  return sessionStorage.getItem(dismissedKey(key)) === "true"
}

export function IntegrationBanners() {
  const [dismissed, setDismissed] = useState<Record<string, boolean>>(() => ({
    ringcentral: isDismissedInSession("ringcentral"),
    currentclient: isDismissedInSession("currentclient"),
    myrepchat: isDismissedInSession("myrepchat"),
    dialpad: isDismissedInSession("dialpad"),
    intulse: isDismissedInSession("intulse"),
    goto: isDismissedInSession("goto"),
    zoomphone: isDismissedInSession("zoomphone"),
    email: isDismissedInSession("email"),
  }))

  const dismiss = (key: string) => {
    sessionStorage.setItem(dismissedKey(key), "true")
    setDismissed((prev) => ({ ...prev, [key]: true }))
  }

  return (
    <>
      {!dismissed.ringcentral && <RingCentralBanner onDismiss={() => dismiss("ringcentral")} />}
      {!dismissed.currentclient && <CurrentClientBanner onDismiss={() => dismiss("currentclient")} />}
      {!dismissed.myrepchat && <MyRepChatBanner onDismiss={() => dismiss("myrepchat")} />}
      {!dismissed.dialpad && <DialpadBanner onDismiss={() => dismiss("dialpad")} />}
      {!dismissed.intulse && <IntulseBanner onDismiss={() => dismiss("intulse")} />}
      {!dismissed.goto && <GoToBanner onDismiss={() => dismiss("goto")} />}
      {!dismissed.zoomphone && <ZoomPhoneBanner onDismiss={() => dismiss("zoomphone")} />}
      {!dismissed.email && <EmailBanner onDismiss={() => dismiss("email")} />}
    </>
  )
}

function RingCentralBanner({ onDismiss }: { onDismiss: () => void }) {
  const { data: integrations, isLoading } = useFetchRingCentralIntegrations()
  const authMutation = useGetRingCentralAuthUri()

  const currentUserIntegration = integrations?.find((i) => i.is_current_user)
  if (isLoading || !currentUserIntegration?.needs_reauth) return null

  const handleReauth = async () => {
    const returnPath = window.location.pathname
    const { auth_uri } = await authMutation.mutateAsync({ returnUrl: returnPath })
    window.location.href = auth_uri
  }

  return (
    <IntegrationBanner
      title="RingCentral requires re-authentication."
      description="Please re-authenticate to continue syncing your RingCentral connection."
      actionLabel="Re-authenticate"
      onAction={handleReauth}
      onDismiss={onDismiss}
      isActionPending={authMutation.isPending}
    />
  )
}

function CurrentClientBanner({ onDismiss }: { onDismiss: () => void }) {
  const { data: integrations, isLoading } = useFetchCurrentClientIntegrations()
  const authMutation = useGetCurrentClientAuthUri()

  const invalidIntegrations = integrations?.filter((i) => i.needs_reauth) ?? []
  if (isLoading || invalidIntegrations.length === 0) return null

  const handleReauth = async (loginHint?: string) => {
    const returnPath = window.location.pathname
    const { auth_uri } = await authMutation.mutateAsync({ returnUrl: returnPath, loginHint })
    window.location.replace(auth_uri)
  }

  const accountNames = invalidIntegrations.map((i) => i.account_name).filter(Boolean) as string[]
  const hasAccountNames = accountNames.length > 0

  return (
    <IntegrationBanner
      title="CurrentClient requires re-authentication."
      description={
        hasAccountNames ? (
          <>
            Please re-authenticate{" "}
            {accountNames.map((name, i) => (
              <span key={name}>
                {i > 0 && ", "}
                <button
                  className="font-semibold underline"
                  onClick={() => handleReauth(name)}
                  disabled={authMutation.isPending}
                >
                  {name}
                </button>
              </span>
            ))}{" "}
            to continue syncing its inboxes.
          </>
        ) : undefined
      }
      actionLabel="Re-authenticate"
      onAction={() => handleReauth(accountNames.length === 1 ? accountNames[0] : undefined)}
      onDismiss={onDismiss}
      isActionPending={authMutation.isPending}
    />
  )
}

function MyRepChatBanner({ onDismiss }: { onDismiss: () => void }) {
  const { data: integrations, isLoading } = useFetchMyRepChatIntegrations()
  const router = useRouter()

  const hasInvalidAuth = integrations?.some((i) => i.needs_reauth)
  if (isLoading || !hasInvalidAuth) return null

  return (
    <IntegrationBanner
      title="MyRepChat requires re-authentication."
      description="Please update your API keys to continue syncing."
      actionLabel="Update API keys"
      onAction={() => router.push("/settings/phone?reauth=myrepchat")}
      onDismiss={onDismiss}
    />
  )
}

function DialpadBanner({ onDismiss }: { onDismiss: () => void }) {
  const { data: integrations, isLoading } = useFetchDialpadIntegrations()
  const authMutation = useGetDialpadAuthUri()

  const invalidIntegrations = integrations?.filter((i) => i.needs_reauth) ?? []
  if (isLoading || invalidIntegrations.length === 0) return null

  const handleReauth = async () => {
    const returnPath = window.location.pathname
    const { auth_uri } = await authMutation.mutateAsync({ returnUrl: returnPath })
    window.location.href = auth_uri
  }

  return (
    <IntegrationBanner
      title="Dialpad requires re-authentication."
      description="Please re-authenticate to continue syncing your Dialpad connection."
      actionLabel="Re-authenticate"
      onAction={handleReauth}
      onDismiss={onDismiss}
      isActionPending={authMutation.isPending}
    />
  )
}

function IntulseBanner({ onDismiss }: { onDismiss: () => void }) {
  const { data: integrations, isLoading } = useFetchIntulseIntegrations()
  const authMutation = useGetIntulseAuthUri()

  const invalidIntegrations = integrations?.filter((i) => i.needs_reauth) ?? []
  if (isLoading || invalidIntegrations.length === 0) return null

  const handleReauth = async () => {
    const returnPath = window.location.pathname
    const { auth_uri } = await authMutation.mutateAsync({ returnUrl: returnPath })
    window.location.href = auth_uri
  }

  return (
    <IntegrationBanner
      title="Intulse requires re-authentication."
      description="Please re-authenticate to continue syncing your Intulse connection."
      actionLabel="Re-authenticate"
      onAction={handleReauth}
      onDismiss={onDismiss}
      isActionPending={authMutation.isPending}
    />
  )
}

function GoToBanner({ onDismiss }: { onDismiss: () => void }) {
  const { data: integrations, isLoading } = useFetchGoToIntegrations()
  const authMutation = useGetGoToAuthUri()

  const currentUserIntegration = integrations?.find((integration) => integration.is_current_user)
  if (isLoading || !currentUserIntegration?.needs_reauth) return null

  const handleReauth = async () => {
    const returnPath = window.location.pathname
    const { auth_uri } = await authMutation.mutateAsync({ returnUrl: returnPath })
    window.location.href = auth_uri
  }

  return (
    <IntegrationBanner
      title="GoTo Connect requires re-authentication."
      description="Please re-authenticate to continue syncing your GoTo Connect account."
      actionLabel="Re-authenticate"
      onAction={handleReauth}
      onDismiss={onDismiss}
      isActionPending={authMutation.isPending}
    />
  )
}

function ZoomPhoneBanner({ onDismiss }: { onDismiss: () => void }) {
  const { data: integrations, isLoading } = useFetchZoomPhoneIntegrations()
  const authMutation = useGetZoomPhoneAuthUri()

  const integration = integrations?.find((item) => item.needs_reauth)
  if (isLoading || !integration) return null

  const handleReauth = async () => {
    const returnPath = window.location.pathname
    const { auth_uri } = await authMutation.mutateAsync({
      returnUrl: returnPath,
      integrationGuid: integration.guid,
    })
    window.location.replace(auth_uri)
  }

  const account = integration.account_email || integration.display_name || integration.zoom_user_id || "this account"

  return (
    <IntegrationBanner
      title="Zoom Phone requires re-authentication."
      description={`Please re-authenticate ${account} to continue syncing its Zoom Phone connection.`}
      actionLabel="Re-authenticate"
      onAction={handleReauth}
      onDismiss={onDismiss}
      isActionPending={authMutation.isPending}
    />
  )
}

function EmailBanner({ onDismiss }: { onDismiss: () => void }) {
  const { data: integrations, isLoading } = useEmailIntegrations()
  const authMutation = useGetEmailAuthUriMutation()

  const invalidIntegrations = integrations?.filter((i) => i.needs_reauth) ?? []
  if (isLoading || invalidIntegrations.length === 0) return null

  const integration = invalidIntegrations[0]

  const handleReauth = async () => {
    const returnPath = window.location.pathname
    const { auth_uri } = await authMutation.mutateAsync({
      provider: integration.provider,
      returnTo: returnPath,
      emailIntegrationId: integration.id,
    })
    window.location.href = auth_uri
  }

  return (
    <IntegrationBanner
      title="Email requires re-authentication."
      description={`Please re-authenticate ${integration.connected_email} to continue syncing your email account.`}
      actionLabel="Re-authenticate"
      onAction={handleReauth}
      onDismiss={onDismiss}
      isActionPending={authMutation.isPending}
    />
  )
}
